import { useState, useCallback, useMemo, memo } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserButton } from "@clerk/clerk-react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Plus, Edit, Trash2, Eye, MessageSquare, DollarSign, MapPin, Loader2, Heart, Search, CheckCircle, TrendingUp, Bot, Award } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { Id } from "../../../convex/_generated/dataModel";
import Paywall from "@/components/Paywall";
import { PromotionSpotlight } from "@/components/PromotionSpotlight";
import { SUBSCRIPTION_PRICE_IDS } from "@/lib/subscriptions";
import type { SubscriptionPlan } from "@/lib/subscriptions";
import { Navbar } from "@/components/Navbar";

// Memoized metric card for analytics
const MetricCard = memo(function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/70 bg-background p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
    </div>
  );
});

// Currency and number formatters (created once, reused)
const currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export default function Dashboard() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<"ads" | "wants" | "responses" | "notifications" | "favorites">("ads");
  const convexApi = api as any;

  // Convex queries
  const userAds = useQuery(api.ads.getUserAds);
  const myAds = (userAds ?? []) as any[];
  const userWants = useQuery(api.wanted.getMyWantedItems, { status: undefined });
  const wants = (userWants ?? []) as any[];
  const responses = useQuery(api.interactions.getUserResponses);
  const responseItems = (responses ?? []) as any[];
  const notifications = useQuery(api.interactions.getUserNotifications);
  const notificationItems = (notifications ?? []) as any[];
  const favorites = useQuery(api.favorites.getFavorites);
  const favoriteItems = (favorites ?? []) as any[];
  const rewardBalance = useQuery(convexApi.rewards.getBalance);
  const surveyCampaigns = useQuery(convexApi.surveys.listActiveCampaigns, { audience: "seller" });
  const activeSurveyItems = (surveyCampaigns ?? []) as any[];

  // Convex mutations
  const deleteAdMutation = useMutation(api.ads.deleteAd);
  const markAsReadMutation = useMutation(api.interactions.markNotificationAsRead);
  const cancelWantMutation = useMutation(api.wanted.cancelWantedItem);
  const foundWantMutation = useMutation(api.wanted.markAsFound);
  
  // Stripe Action
  const createCheckoutSession = useAction(api.stripe.createCheckoutSession);

  // Memoized handlers with useCallback
  const handleDeleteAd = useCallback(async (id: Id<"ads">) => {
    if (!confirm(t('dashboard.confirmDelete'))) return;

    try {
      await deleteAdMutation({ id });
      toast.success(t('dashboard.adDeleted'));
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  }, [deleteAdMutation, t]);

  const handlePromoteAd = useCallback(async (adId: Id<"ads">) => {
    try {
      const url = await createCheckoutSession({
        priceId: "price_1Q...AdBoost", // REPLACE WITH REAL PRICE ID
        mode: "payment",
        successUrl: `${window.location.origin}/dashboard?promoted=true`,
        cancelUrl: `${window.location.origin}/dashboard`,
        metadata: {
          adId: adId,
          type: "promotion",
        },
      });
      if (url) window.location.href = url;
    } catch (error) {
      toast.error("Failed to initiate promotion");
      console.error(error);
    }
  }, [createCheckoutSession]);

  const handleActivateSalesman = useCallback(async (adId: Id<"ads">) => {
    try {
      const url = await createCheckoutSession({
        priceId: "price_1Q...SalesmanBot", // REPLACE WITH REAL PRICE ID
        mode: "payment",
        successUrl: `${window.location.origin}/dashboard?salesman=active`,
        cancelUrl: `${window.location.origin}/dashboard`,
        metadata: {
          adId: adId,
          type: "salesman_bot",
        },
      });
      if (url) window.location.href = url;
    } catch (error) {
       toast.error("Failed to hire Salesman Bot");
       console.error(error);
    }
  }, [createCheckoutSession]);

  const handleDeleteWant = useCallback(async (id: Id<"wantedItems">) => {
    if (!confirm(t('dashboard.confirmDeleteWant'))) return;

    try {
      await cancelWantMutation({ id });
      toast.success(t('dashboard.wantDeleted'));
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  }, [cancelWantMutation, t]);

  const handleFoundWant = useCallback(async (id: Id<"wantedItems">) => {
    try {
      await foundWantMutation({ id });
      toast.success(t('dashboard.wantFound'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  }, [foundWantMutation, t]);

  const handleMarkAsRead = useCallback(async (id: Id<"notifications">) => {
    try {
      await markAsReadMutation({ id });
    } catch (error) {
      toast.error(t('errors.markReadFailed'));
    }
  }, [markAsReadMutation, t]);

  const handleUpgradeSubscription = useCallback(async (plan: SubscriptionPlan) => {
    const priceId = SUBSCRIPTION_PRICE_IDS[plan];
    if (!priceId) {
      toast.error("Subscription plan is not configured.");
      return;
    }

    try {
      const url = await createCheckoutSession({
        priceId,
        mode: "subscription",
        successUrl: `${window.location.origin}/dashboard?plan=${plan}&status=success`,
        cancelUrl: `${window.location.origin}/dashboard?plan=${plan}&status=cancelled`,
        metadata: {
          planType: plan,
          type: "subscription",
        },
      });

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      toast.error("Unable to start subscription checkout.");
      console.error(error);
    }
  }, [createCheckoutSession]);

  // Memoized computed values
  const analytics = useMemo(() => {
    const now = Date.now();
    const totalInventoryValue = myAds.reduce((acc, ad) => acc + (ad.price ?? 0), 0);
    const totalViews = myAds.reduce((acc, ad) => acc + (ad.views ?? 0), 0);
    const activePromotions = myAds.filter((ad) => ad.promotedUntil && ad.promotedUntil > now).length;
    const salesmanCampaigns = myAds.filter((ad) => ad.features?.salesman_bot).length;
    
    return {
      totalInventoryValue,
      totalViews,
      averageViews: myAds.length > 0 ? Math.round(totalViews / myAds.length) : 0,
      activePromotions,
      salesmanCampaigns,
    };
  }, [myAds]);

  const rewardPoints = rewardBalance?.points ?? 0;

  // Loading state
  if (userAds === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Memoized metrics for analytics preview
  const analyticsMetrics = useMemo(() => [
    { label: "Inventory value", value: currencyFormatter.format(analytics.totalInventoryValue) },
    { label: "Total views", value: numberFormatter.format(analytics.totalViews) },
    { label: "Active promotions", value: numberFormatter.format(analytics.activePromotions) },
    { label: "Salesman AI campaigns", value: numberFormatter.format(analytics.salesmanCampaigns) },
  ], [analytics]);

  const proAnalyticsPreview = (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {analyticsMetrics.map((metric) => (
        <div key={metric.label} className="rounded-xl border border-border/70 bg-background p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{metric.label}</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{metric.value}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container py-8">
        <h1 className="mb-8">{t('nav.dashboard')}</h1>

        <Paywall
          tier="pro_seller"
          subtitle="See advanced analytics, automate boosts, and bundle Salesman AI credits every month."
          className="mb-10"
          perks={[
            { label: "Auto-boost 3 listings daily" },
            { label: "Historical performance charts" },
            { label: "Monthly Salesman AI credit bundle" },
            { label: "Priority trust & safety response" },
          ]}
          onCtaClick={() => handleUpgradeSubscription("pro_seller")}
          ctaLabel="Upgrade to Pro Seller"
        >
          {proAnalyticsPreview}
        </Paywall>

        <PromotionSpotlight
          title="Scale your seller toolkit"
          description="Pair boosts, automation, and protection to grow faster."
          audience="seller"
          className="mb-10"
        />

        <Card className="mb-10 border-sky-400/30 bg-sky-400/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Award className="h-5 w-5 text-sky-500" />
              Earn marketplace rewards
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              You currently have <span className="font-semibold text-foreground">{rewardPoints}</span> Listo points ready to redeem for upgrades.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeSurveyItems.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {activeSurveyItems.slice(0, 4).map((campaign) => (
                  <div
                    key={campaign._id}
                    className="rounded-xl border border-border/60 bg-background/70 p-4 shadow-sm"
                  >
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-foreground">{campaign.title}</p>
                      {campaign.description ? (
                        <p className="text-xs text-muted-foreground line-clamp-3">
                          {campaign.description}
                        </p>
                      ) : null}
                      <p className="text-xs font-medium text-sky-600">
                        +{campaign.rewardValue ?? 0} pts · {campaign.audience?.join(", ") ?? "All traders"}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="mt-3" asChild>
                      <Link href="/surveys">
                        <a>View survey</a>
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No active surveys right now—check back soon for new research missions and rewards.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-border overflow-x-auto">
          <button
            onClick={() => setSelectedTab("ads")}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${
              selectedTab === "ads"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t('dashboard.myAds')} ({userAds?.length || 0})
          </button>
          <button
            onClick={() => setSelectedTab("wants")}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${
              selectedTab === "wants"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t('dashboard.myWants')} ({userWants?.length || 0})
          </button>
          <button
            onClick={() => setSelectedTab("responses")}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${
              selectedTab === "responses"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t('dashboard.responses')} ({responses?.length || 0})
          </button>
          <button
            onClick={() => setSelectedTab("favorites")}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${
              selectedTab === "favorites"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t('dashboard.favorites')} ({favorites?.length || 0})
          </button>
          <button
            onClick={() => setSelectedTab("notifications")}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${
              selectedTab === "notifications"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t('dashboard.notifications')} ({notificationItems.filter((n) => !n.isRead).length || 0})
          </button>
        </div>

        {/* My Ads Tab */}
        {selectedTab === "ads" && (
          <div className="space-y-4">
            {myAds.length > 0 ? (
              myAds.map((ad) => (
                <div key={ad._id} className="card-elegant p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">{ad.title}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                        {ad.description}
                      </p>

                      <div className="flex gap-4 text-sm">
                        {ad.price && (
                          <div className="flex items-center gap-1 text-primary font-medium">
                            <DollarSign className="w-4 h-4" />
                            ${ad.price.toFixed(2)}
                          </div>
                        )}
                        {ad.location && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            {ad.location}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Eye className="w-4 h-4" />
                          {ad.views} {t('ad.views')}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ad.status === 'active'
                            ? 'bg-accent/10 text-accent'
                            : ad.status === 'pending_approval'
                            ? 'bg-secondary/10 text-secondary'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {t(`ad.status.${ad.status}`)}
                        </span>
                        {/* Promoting Indicator */}
                        {ad.promotedUntil && (ad.promotedUntil > Date.now()) && (
                           <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                             <TrendingUp className="w-3 h-3" /> Promoted
                           </span>
                        )}
                        {/* Salesman Bot Indicator */}
                        {ad.features?.salesman_bot && (
                           <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                             <Bot className="w-3 h-3" /> Salesman AI
                           </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                       {/* Salesman Bot Button */}
                       {!ad.features?.salesman_bot && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleActivateSalesman(ad._id)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title={t('Hire Salesman AI')}
                          >
                            <Bot className="w-4 h-4" />
                          </Button>
                       )}
                       {/* Promote Button */}
                       <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePromoteAd(ad._id)}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        title={t('Promote Ad')}
                      >
                        <TrendingUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <Link href={`/ads/${ad._id}`}>
                          <a>{t('common.view')}</a>
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toast.info(t('common.comingSoon'))}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAd(ad._id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">{t('dashboard.noAds')}</p>
                <Button asChild>
                  <Link href="/post">
                    <a>{t('dashboard.postFirstAd')}</a>
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Wants Tab */}
        {selectedTab === "wants" && (
          <div className="space-y-4">
            {userWants && userWants.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {wants.map((want) => (
                  <div key={want._id} className="card-elegant p-4 relative group">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-lg line-clamp-1">{want.title}</h3>
                      <div className="flex gap-2">
                         {want.status === "active" && (
                           <button 
                             onClick={() => handleFoundWant(want._id)}
                             className="p-1.5 text-muted-foreground hover:text-green-500 transition-colors"
                             title={t('dashboard.wantFound')}
                           >
                             <CheckCircle className="w-4 h-4" />
                           </button>
                         )}
                         <button 
                           onClick={() => handleDeleteWant(want._id)}
                           className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                           title={t('common.delete')}
                         >
                           <Trash2 className="w-4 h-4" />
                         </button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4 h-10">
                      {want.description || t('wants.placeholderDesc')}
                    </p>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                         <span className="text-muted-foreground">{t('wants.urgency')}:</span>
                         <span className={`capitalize ${
                           want.urgency === 'high' ? 'text-red-500 font-medium' :
                           want.urgency === 'medium' ? 'text-orange-500' : 'text-green-500'
                         }`}>
                           {want.urgency}
                         </span>
                      </div>
                      
                      {(want.minPrice || want.maxPrice) && (
                        <div className="flex items-center gap-1 text-primary font-medium">
                          <DollarSign className="w-3 h-3" />
                          {want.minPrice || 0} - {want.maxPrice || "Any"}
                        </div>
                      )}

                      <div className="pt-2 border-t border-border flex justify-between items-center text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${
                          want.status === 'active' ? 'bg-primary/10 text-primary' :
                          want.status === 'found' ? 'bg-green-100 text-green-700' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {want.status === 'active' ? t('dashboard.active') :
                           want.status === 'found' ? t('dashboard.found') : t('dashboard.expired')}
                        </span>
                        
                        {(want.matchCount > 0) && (
                           <span className="text-primary font-medium flex items-center gap-1">
                             <Search className="w-3 h-3" /> {want.matchCount} Matches
                           </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">{t('dashboard.noWants')}</p>
                <Link href="/browse">
                  <Button variant="outline">
                    {t('wants.createButton')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Responses Tab */}
        {selectedTab === "responses" && (
          <div className="space-y-4">
            {responses && responses.length > 0 ? (
              responseItems.map((response) => (
                <div key={response._id} className="card-elegant p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-primary" />
                        <p className="font-medium">{t('dashboard.responseToAd')}</p>
                      </div>
                      {response.message && (
                        <p className="text-muted-foreground mb-2">{response.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {new Date(response.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      response.status === 'new'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {response.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t('dashboard.noResponses')}</p>
              </div>
            )}
          </div>
        )}

        {/* Favorites Tab */}
        {selectedTab === "favorites" && (
          <div className="space-y-4">
            {favorites && favorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteItems.map((fav) => fav.ad && (
                  <Link key={fav._id} href={`/ads/${fav.ad.id}`}>
                    <a className="card-elegant overflow-hidden hover:shadow-lg transition-shadow group">
                      <div className="w-full h-40 bg-muted flex items-center justify-center overflow-hidden relative">
                        {fav.ad.images && fav.ad.images.length > 0 ? (
                          <img
                            src={fav.ad.images[0]}
                            alt={fav.ad.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="text-muted-foreground">{t('ad.noImage')}</div>
                        )}
                        <Heart className="absolute top-2 right-2 w-5 h-5 text-red-500 fill-red-500" />
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold line-clamp-2 mb-2">{fav.ad.title}</h3>
                        {fav.ad.price && (
                          <div className="flex items-center gap-1 text-primary font-semibold">
                            <DollarSign className="w-4 h-4" />
                            ${fav.ad.price.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </a>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">{t('dashboard.noFavorites')}</p>
                <Button asChild variant="outline">
                  <Link href="/browse">
                    <a>{t('home.browseListings')}</a>
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {selectedTab === "notifications" && (
          <div className="space-y-4">
            {notifications && notifications.length > 0 ? (
              notificationItems.map((notification) => (
                <div
                  key={notification._id}
                  className={`card-elegant p-6 cursor-pointer transition-colors ${
                    !notification.isRead ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => !notification.isRead && handleMarkAsRead(notification._id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1">{notification.title}</h4>
                      {notification.message && (
                        <p className="text-muted-foreground text-sm mb-2">{notification.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    {!notification.isRead && (
                      <div className="w-2 h-2 rounded-full bg-primary mt-1" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t('dashboard.noNotifications')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
